import React, { useState, useEffect } from 'react';
import {
    Paper,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    Box,
    Chip,
    IconButton,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    TablePagination,
    FormControl,
    Select,
    MenuItem,
    InputLabel,
    TextField,
    Input,
    Checkbox,
    Toolbar,
    TableSortLabel,
    Collapse,
    Stack,
    Avatar
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import {
    GetApp as ExportIcon, CheckCircle as ConfirmIcon, Search as SearchIcon, Cancel as CancelIcon,
    Edit as EditIcon, Save as SaveIcon, Close as CloseIcon, RestoreIcon, Replay as ReplayIcon,
    Groups as GroupsIcon, ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon,
    WhatsApp as WhatsAppIcon, Add as AddIcon, Remove as RemoveIcon
} from '@mui/icons-material';
import { bookingAPI } from '../services/api';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import { formatTimeRange } from '../utils/timeUtils';
import { datePickerStyles, datePickerPopperStyles } from '../styles/datePickerStyles';
import { darkSelectMenuProps } from '../styles/selectMenuStyles';

// Logo color palette
const logoColors = {
    primaryBlue: '#3f51b5',
    brightYellow: '#c4d835',
    darkBlue: '#2c387e',
    lightYellow: '#d4e157',
};

const BookingList = ({ refreshTrigger }) => {
    const [bookings, setBookings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [confirmDialog, setConfirmDialog] = useState({ open: false, booking: null });
    const [cancelDialog, setCancelDialog] = useState({ open: false, booking: null });
    const [updatingStatus, setUpdatingStatus] = useState(null);
    const [editingBooking, setEditingBooking] = useState(null);
    const [editForm, setEditForm] = useState({});

    // Shared-booking ledger panel: which booking's panel is open, and its editable draft.
    const [expandedLedgerId, setExpandedLedgerId] = useState(null);
    const [ledgerDraft, setLedgerDraft] = useState(null);
    const [savingLedger, setSavingLedger] = useState(false);
    
    // Pagination state
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    
    // Search state
    const [searchTerm, setSearchTerm] = useState('');
    
    // Status filter state
    const [statusFilter, setStatusFilter] = useState([]);

    // Sort state - defaults to Date ascending (Time ascending as the tie-break)
    const [sortConfig, setSortConfig] = useState({ field: 'date', direction: 'asc' });
    
    // Multi-selection state
    const [selectedBookings, setSelectedBookings] = useState([]);
    const [bulkConfirmDialog, setBulkConfirmDialog] = useState({ open: false, bookings: [] });
    const [bulkCancelDialog, setBulkCancelDialog] = useState({ open: false, bookings: [] });
    const [bulkProcessing, setBulkProcessing] = useState(false);

    const fetchBookings = async () => {
        try {
            const response = await bookingAPI.getAllBookings();
            let fetchedBookings = response.data || [];
            
            // Auto-complete bookings in real-time
            const now = new Date();
            fetchedBookings = fetchedBookings.map(booking => {
                if (booking.status === 'CONFIRMED') {
                    const bookingEndTime = new Date(`${booking.bookingDate}T${booking.endTime}`);
                    const oneHourAfterEnd = new Date(bookingEndTime.getTime() + (60 * 60 * 1000));
                    
                    if (now > oneHourAfterEnd) {
                        return { ...booking, status: 'COMPLETED' };
                    }
                }
                return booking;
            });
            
            setBookings(fetchedBookings);
        } catch (error) {
            toast.error('Failed to fetch bookings');
            console.error('Error fetching bookings:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchBookings();
    }, [refreshTrigger]);
    
    // Real-time status checking - check every minute
    useEffect(() => {
        const interval = setInterval(() => {
            fetchBookings();
        }, 60000); // Check every minute
        
        return () => clearInterval(interval);
    }, []);

    const handleConfirmPayment = async () => {
        try {
            setUpdatingStatus(confirmDialog.booking.id);
            await bookingAPI.updateBookingStatus(confirmDialog.booking.id, 'CONFIRMED');
            toast.success('Booking status updated to confirmed');
            fetchBookings();
            setConfirmDialog({ open: false, booking: null });
        } catch (error) {
            toast.error('Failed to update booking status');
            console.error('Error updating booking status:', error);
        } finally {
            setUpdatingStatus(null);
        }
    };

    const handleCancelBooking = async () => {
        try {
            setUpdatingStatus(cancelDialog.booking.id);
            await bookingAPI.cancelBooking(cancelDialog.booking.id);
            toast.success('Booking cancelled successfully');
            fetchBookings();
            setCancelDialog({ open: false, booking: null });
        } catch (error) {
            toast.error('Failed to cancel booking');
            console.error('Error cancelling booking:', error);
        } finally {
            setUpdatingStatus(null);
        }
    };

    const handleExport = async () => {
        try {
            const response = await bookingAPI.exportToExcel();
            const blob = new Blob([response.data], { 
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
            });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `bookings-${dayjs().format('YYYY-MM-DD')}.xlsx`;
            document.body.appendChild(link);
            link.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(link);
            toast.success('Export completed successfully');
        } catch (error) {
            toast.error('Failed to export bookings');
            console.error('Error exporting:', error);
        }
    };
    
    // Editing functions
    const handleEditBooking = (booking) => {
        setEditingBooking(booking.id);
        setEditForm({
            bookingDate: booking.bookingDate,
            startTime: booking.startTime,
            endTime: booking.endTime,
            courtNumber: booking.courtNumber,
            totalAmount: booking.totalAmount,
            status: booking.status, // Include status in edit form
            opponentName: booking.opponentName || '',
            paidCashAmount: booking.paidCashAmount || 0,
            paidOnlineAmount: booking.paidOnlineAmount || 0,
            remarks: booking.remarks || ''
        });
    };
    
    const handleCancelEdit = () => {
        setEditingBooking(null);
        setEditForm({});
    };
    
    const handleConfirmEdit = async (bookingId) => {
        try {
            setUpdatingStatus(bookingId);
            
            // Get the booking to check if status changed
            const booking = bookings.find(b => b.id === bookingId);
            const statusChanged = editForm.status && editForm.status !== booking.status;
            
            // First, update the booking fields
            await bookingAPI.editBooking(bookingId, {
                bookingDate: editForm.bookingDate,
                startTime: editForm.startTime,
                endTime: editForm.endTime,
                courtNumber: editForm.courtNumber,
                totalAmount: editForm.totalAmount,
                opponentName: editForm.opponentName,
                paidCashAmount: editForm.paidCashAmount,
                paidOnlineAmount: editForm.paidOnlineAmount,
                remarks: editForm.remarks,
                reason: `Admin updated booking details${statusChanged ? ' and status' : ''}`
            });
            
            // Then, if status changed, update the status
            if (statusChanged) {
                await bookingAPI.updateBookingStatus(bookingId, editForm.status);
            }
            
            toast.success(`Booking updated successfully${statusChanged ? ' with new status' : ''}`);
            fetchBookings();
            setEditingBooking(null);
            setEditForm({});
        } catch (error) {
            toast.error('Failed to update booking: ' + (error.response?.data?.message || error.message));
            console.error('Error updating booking:', error);
        } finally {
            setUpdatingStatus(null);
        }
    };
    
    const handleEditFormChange = (field, value) => {
        setEditForm(prev => ({
            ...prev,
            [field]: value
        }));
    };

    // --- Shared-booking ledger panel ---
    const toggleLedgerPanel = (booking) => {
        if (expandedLedgerId === booking.id) {
            setExpandedLedgerId(null);
            setLedgerDraft(null);
            return;
        }
        setExpandedLedgerId(booking.id);
        const existing = booking.participants && booking.participants.length === 6
            ? booking.participants
            : Array.from({ length: 6 }, (_, i) => booking.participants?.[i] || { name: '', phone: '', amountPaid: 0 });
        setLedgerDraft({
            additionalGuestsCount: booking.additionalGuestsCount || 0,
            participants: existing.map(p => ({ name: p.name || '', phone: p.phone || '', amountPaid: p.amountPaid || 0 })),
        });
    };

    const updateLedgerParticipant = (index, field, value) => {
        setLedgerDraft(prev => {
            const next = [...prev.participants];
            next[index] = { ...next[index], [field]: value };
            return { ...prev, participants: next };
        });
    };

    const ledgerSplitCount = (draft) => Math.max(6 + (draft?.additionalGuestsCount || 0), 1);
    const ledgerShare = (booking, draft) => booking.totalAmount ? Math.round((booking.totalAmount / ledgerSplitCount(draft)) * 100) / 100 : 0;

    const saveLedger = async (booking) => {
        const nameErrors = ledgerDraft.participants.some(p => !p.name || !p.name.trim());
        if (nameErrors) {
            toast.error('All 6 participant names are required');
            return;
        }
        if (!ledgerDraft.participants[0].phone || !ledgerDraft.participants[0].phone.trim()) {
            toast.error('Person 1: phone number is required');
            return;
        }
        setSavingLedger(true);
        try {
            await bookingAPI.editBooking(booking.id, {
                isShared: true,
                additionalGuestsCount: ledgerDraft.additionalGuestsCount,
                participants: ledgerDraft.participants,
                reason: 'Admin updated shared booking split/ledger',
            });
            toast.success('Split & ledger updated');
            fetchBookings();
            setExpandedLedgerId(null);
            setLedgerDraft(null);
        } catch (error) {
            toast.error('Failed to update ledger: ' + (error.response?.data?.message || error.message));
        } finally {
            setSavingLedger(false);
        }
    };

    const makeShared = async (booking) => {
        setSavingLedger(true);
        try {
            // Don't send participants here - the backend correctly rejects blank names, and this
            // step is just "turn splitting on", before the admin has typed anything. The actual
            // 6-names-required check happens on Save, once the panel below is filled in.
            await bookingAPI.editBooking(booking.id, {
                isShared: true,
                additionalGuestsCount: 0,
                reason: 'Admin converted booking to shared',
            });
            fetchBookings();
            toggleLedgerPanel({ ...booking, isShared: true, additionalGuestsCount: 0, participants: [] });
        } catch (error) {
            toast.error('Failed to enable split: ' + (error.response?.data?.message || error.message));
        } finally {
            setSavingLedger(false);
        }
    };

    const whatsAppReminderLink = (booking, participant, remaining) => {
        const digits = (participant.phone || '').replace(/[^\d+]/g, '').replace(/^\+/, '');
        const dateStr = booking.bookingDate ? dayjs(booking.bookingDate).format('DD/MM/YYYY') : '';
        const timeStr = formatTimeRange ? formatTimeRange(booking.startTime, booking.endTime) : `${booking.startTime}-${booking.endTime}`;
        const message =
            `Hi ${participant.name}! This is a reminder for the futsal booking on ${dateStr} (${timeStr}), Court ${booking.courtNumber}.\n` +
            `Your share of the bill is Rs ${remaining.toLocaleString()}. Please send it across whenever you can. Thanks!`;
        return `https://wa.me/${digits}?text=${encodeURIComponent(message)}`;
    };

    // Pagination handlers
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Multi-selection handlers
    const handleSelectAll = (event) => {
        if (event.target.checked) {
            setSelectedBookings(paginatedFilteredBookings.map(booking => booking.id));
        } else {
            setSelectedBookings([]);
        }
    };

    const handleSelectBooking = (bookingId) => {
        setSelectedBookings(prev => 
            prev.includes(bookingId) 
                ? prev.filter(id => id !== bookingId)
                : [...prev, bookingId]
        );
    };

    const handleBulkConfirm = () => {
        const pendingBookings = paginatedFilteredBookings.filter(booking => 
            selectedBookings.includes(booking.id) && booking.status === 'PENDING'
        );
        setBulkConfirmDialog({ open: true, bookings: pendingBookings });
    };

    const handleBulkCancel = () => {
        const cancellableBookings = paginatedFilteredBookings.filter(booking => 
            selectedBookings.includes(booking.id) && 
            booking.status !== 'CANCELLED' // All statuses except CANCELLED can be cancelled
        );
        setBulkCancelDialog({ open: true, bookings: cancellableBookings });
    };

    const executeBulkConfirm = async () => {
        try {
            setBulkProcessing(true);
            const confirmPromises = bulkConfirmDialog.bookings.map(booking => 
                bookingAPI.updateBookingStatus(booking.id, 'CONFIRMED')
            );
            
            await Promise.all(confirmPromises);
            toast.success(`Successfully confirmed ${bulkConfirmDialog.bookings.length} booking(s)`);
            setBulkConfirmDialog({ open: false, bookings: [] });
            setSelectedBookings([]);
            fetchBookings();
        } catch (error) {
            toast.error('Failed to confirm some bookings');
            console.error('Bulk confirmation error:', error);
        } finally {
            setBulkProcessing(false);
        }
    };

    const executeBulkCancel = async () => {
        try {
            setBulkProcessing(true);
            const cancelPromises = bulkCancelDialog.bookings.map(booking => 
                bookingAPI.cancelBooking(booking.id)
            );
            
            await Promise.all(cancelPromises);
            toast.success(`Successfully cancelled ${bulkCancelDialog.bookings.length} booking(s)`);
            setBulkCancelDialog({ open: false, bookings: [] });
            setSelectedBookings([]);
            fetchBookings();
        } catch (error) {
            toast.error('Failed to cancel some bookings');
            console.error('Bulk cancellation error:', error);
        } finally {
            setBulkProcessing(false);
        }
    };
    
    // Search handler
    const handleSearchChange = (event) => {
        setSearchTerm(event.target.value);
        setPage(0); // Reset to first page when searching
    };
    
    // Status filter handler
    const handleStatusFilterChange = (event) => {
        setStatusFilter(event.target.value);
        setPage(0);
    };

    // Sort handler - clicking the same column toggles direction, switching column defaults to ascending
    const handleSort = (field) => {
        setSortConfig(prev => ({
            field,
            direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
        setPage(0);
    };

    // Calculate paginated data
    const paginatedBookings = bookings.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
    
    // Search and status filter function
    const filteredBookings = bookings.filter(booking => {
        // Apply status filter
        const matchesStatus = statusFilter.length === 0 || statusFilter.includes(booking.status);
        
        // Apply search filter
        if (!searchTerm.trim()) return matchesStatus;
        
        const searchLower = searchTerm.toLowerCase().trim();
        
        // Search across multiple fields with better null handling
        const matchesSearch = (
            (booking.id?.toString() || '').toLowerCase().includes(searchLower) ||
            (booking.customerName || '').toLowerCase().includes(searchLower) ||
            (booking.customerEmail || '').toLowerCase().includes(searchLower) ||
            (booking.customerPhone || '').toLowerCase().includes(searchLower) ||
            (booking.opponentName || '').toLowerCase().includes(searchLower) ||
            (booking.remarks || '').toLowerCase().includes(searchLower) ||
            (booking.bookingDate ? dayjs(booking.bookingDate).format('MMM DD, YYYY').toLowerCase() : '').includes(searchLower) ||
            (booking.bookingDate ? dayjs(booking.bookingDate).format('YYYY-MM-DD') : '').includes(searchLower) ||
            (booking.bookingDate ? dayjs(booking.bookingDate).format('DD/MM/YYYY') : '').includes(searchLower) ||
            (booking.startTime && booking.endTime ? formatTimeRange(booking.startTime, booking.endTime).toLowerCase() : '').includes(searchLower) ||
            (booking.startTime || '').toLowerCase().includes(searchLower) ||
            (booking.endTime || '').toLowerCase().includes(searchLower) ||
            (booking.courtNumber ? `Court ${booking.courtNumber}`.toLowerCase() : '').includes(searchLower) ||
            (booking.courtNumber?.toString() || '').includes(searchLower) ||
            (booking.totalAmount ? `PKR ${booking.totalAmount}`.toLowerCase() : '').includes(searchLower) ||
            (booking.totalAmount?.toString() || '').includes(searchLower) ||
            (booking.status || '').toLowerCase().includes(searchLower)
        );
        
        return matchesStatus && matchesSearch;
    });
    
    // Sort filtered results (Date/Time columns are sortable; both default ascending, and each
    // uses the other as a tie-break so same-day/same-time bookings still order sensibly)
    const sortedFilteredBookings = [...filteredBookings].sort((a, b) => {
        const dir = sortConfig.direction === 'asc' ? 1 : -1;
        if (sortConfig.field === 'time') {
            const timeCompare = (a.startTime || '').localeCompare(b.startTime || '');
            if (timeCompare !== 0) return timeCompare * dir;
            return (a.bookingDate || '').localeCompare(b.bookingDate || '') * dir;
        }
        // Default: sort by date
        const dateCompare = (a.bookingDate || '').localeCompare(b.bookingDate || '');
        if (dateCompare !== 0) return dateCompare * dir;
        return (a.startTime || '').localeCompare(b.startTime || '') * dir;
    });

    // Apply pagination to sorted, filtered results
    const paginatedFilteredBookings = sortedFilteredBookings.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    const getStatusColor = (status) => {
        switch (status) {
            case 'CONFIRMED':
                return 'success';
            case 'CANCELLED':
                return 'error';
            case 'CONFLICT':
                return 'error';
            case 'PENDING':
                return 'warning';
            case 'COMPLETED':
                return 'default';
            default:
                return 'default';
        }
    };

    if (loading) {
        return (
            <Paper sx={{ 
                p: 3, 
                m: 2, 
                backgroundColor: logoColors.darkBlue,
                color: logoColors.brightYellow 
            }}>
                <Typography color="inherit">Loading bookings...</Typography>
            </Paper>
        );
    }

    return (
        <Paper elevation={3} sx={{ 
            p: 3, 
            m: 2, 
            backgroundColor: logoColors.darkBlue,
            color: logoColors.brightYellow 
        }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: { xs: 'wrap', sm: 'nowrap' }, gap: { xs: 2, sm: 0 } }}>
                <Typography variant="h5" sx={{ color: logoColors.brightYellow, fontWeight: 'bold' }}>
                    All Bookings ({filteredBookings.length})
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end', flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Typography variant="caption" sx={{ color: logoColors.brightYellow, fontSize: '0.75rem' }}>
                            Rows per page
                        </Typography>
                        <FormControl size="small" sx={{ minWidth: 120 }}>
                            <Select
                                value={rowsPerPage}
                                onChange={handleChangeRowsPerPage}
                                sx={{
                                    color: logoColors.brightYellow,
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: logoColors.brightYellow,
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: logoColors.lightYellow,
                                    },
                                    '& .MuiSvgIcon-root': {
                                        color: logoColors.brightYellow,
                                    }
                                }}
                                MenuProps={darkSelectMenuProps(logoColors)}
                            >
                                <MenuItem value={5}>5</MenuItem>
                                <MenuItem value={10}>10</MenuItem>
                                <MenuItem value={25}>25</MenuItem>
                                <MenuItem value={50}>50</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>
                    <Button
                        variant="contained"
                        startIcon={<ExportIcon />}
                        onClick={handleExport}
                        disabled={bookings.length === 0}
                        sx={{ 
                            backgroundColor: logoColors.brightYellow, 
                            color: logoColors.darkBlue,
                            '&:hover': { 
                                backgroundColor: logoColors.lightYellow 
                            }, 
                            textTransform: 'none',
                            boxShadow: 'none',
                            fontWeight: 'bold',
                            height: '40px' // Match the height of the Select component
                        }}
                    >
                        Export to Excel
                    </Button>
                </Box>
            </Box>

            {/* Search Bar - Always visible */}
            <Box sx={{ mb: 3 }}>
                <TextField
                    fullWidth
                    placeholder="Search by ID, Customer, Contact, Date, Time, Court, Amount, or Status..."
                    value={searchTerm}
                    onChange={handleSearchChange}
                    InputProps={{
                        startAdornment: <SearchIcon sx={{ color: logoColors.brightYellow, mr: 1 }} />,
                    }}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            color: logoColors.brightYellow,
                            '& fieldset': {
                                borderColor: logoColors.brightYellow,
                            },
                            '&:hover fieldset': {
                                borderColor: logoColors.lightYellow,
                            },
                            '&.Mui-focused fieldset': {
                                borderColor: logoColors.lightYellow,
                            },
                        },
                        '& .MuiInputBase-input::placeholder': {
                            color: logoColors.brightYellow + '80',
                            opacity: 1,
                        },
                    }}
                />
            </Box>

            {/* Bulk Action Toolbar */}
            {selectedBookings.length > 0 && (
                <Toolbar sx={{ 
                    backgroundColor: logoColors.darkBlue,
                    borderRadius: 1,
                    mb: 1,
                    px: 2,
                    py: 1,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    <Typography sx={{ 
                        color: logoColors.brightYellow,
                        fontWeight: 'bold'
                    }}>
                        {selectedBookings.length} booking(s) selected
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        {paginatedFilteredBookings.some(booking => 
                            selectedBookings.includes(booking.id) && booking.status === 'PENDING'
                        ) && (
                            <Button
                                onClick={handleBulkConfirm}
                                variant="contained"
                                sx={{
                                    backgroundColor: logoColors.brightYellow,
                                    color: logoColors.darkBlue,
                                    fontWeight: 'bold',
                                    '&:hover': {
                                        backgroundColor: logoColors.lightYellow,
                                    }
                                }}
                            >
                                Confirm Selected
                            </Button>
                        )}
                        {paginatedFilteredBookings.some(booking => 
                            selectedBookings.includes(booking.id) && 
                            booking.status !== 'CANCELLED' // All except CANCELLED can be cancelled
                        ) && (
                            <Button
                                onClick={handleBulkCancel}
                                variant="contained"
                                sx={{
                                    backgroundColor: '#ff9800',
                                    color: 'white',
                                    fontWeight: 'bold',
                                    '&:hover': {
                                        backgroundColor: '#f57c00',
                                    }
                                }}
                            >
                                Cancel Selected
                            </Button>
                        )}
                        <Button
                            onClick={() => setSelectedBookings([])}
                            variant="outlined"
                            sx={{
                                color: logoColors.brightYellow,
                                borderColor: logoColors.brightYellow,
                                '&:hover': {
                                    borderColor: logoColors.lightYellow,
                                    backgroundColor: logoColors.brightYellow + '10',
                                }
                            }}
                        >
                            Clear Selection
                        </Button>
                    </Box>
                </Toolbar>
            )}

            {/* Phone-only controls: the table header (select-all, sort, status filter) is hidden at this size */}
            <Box sx={{
                display: { xs: 'flex', sm: 'none' },
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 1,
                p: 1,
                backgroundColor: logoColors.brightYellow,
                color: logoColors.darkBlue,
                borderRadius: '4px 4px 0 0',
            }}>
                <Checkbox
                    checked={selectedBookings.length > 0 && selectedBookings.length === paginatedFilteredBookings.length}
                    indeterminate={selectedBookings.length > 0 && selectedBookings.length < paginatedFilteredBookings.length}
                    onChange={handleSelectAll}
                    inputProps={{ 'aria-label': 'Select all bookings on this page' }}
                    sx={{ color: logoColors.darkBlue, '&.Mui-checked, &.MuiCheckbox-indeterminate': { color: logoColors.darkBlue } }}
                />
                {['date', 'time'].map((field) => (
                    <Button
                        key={field}
                        size="small"
                        onClick={() => handleSort(field)}
                        sx={{ color: logoColors.darkBlue, fontWeight: 'bold', textTransform: 'capitalize', minHeight: 40 }}
                    >
                        {field}{sortConfig.field === field ? (sortConfig.direction === 'asc' ? ' ↑' : ' ↓') : ''}
                    </Button>
                ))}
                <FormControl variant="standard" size="small" sx={{ minWidth: 90, ml: 'auto' }}>
                    <Select
                        multiple
                        displayEmpty
                        value={statusFilter}
                        onChange={handleStatusFilterChange}
                        renderValue={(selected) => selected.length > 0 ? `Status (${selected.length})` : 'Status'}
                        MenuProps={{
                            autoFocus: false,
                            PaperProps: {
                                sx: {
                                    backgroundColor: logoColors.darkBlue,
                                    '& .MuiMenuItem-root': {
                                        color: logoColors.brightYellow,
                                        '&:hover, &.Mui-selected, &.Mui-selected:hover': { backgroundColor: logoColors.primaryBlue },
                                    },
                                },
                            },
                        }}
                        sx={{
                            color: logoColors.darkBlue,
                            fontWeight: 'bold',
                            '&:before, &:after, &:hover:not(.Mui-disabled):before': { borderBottom: 'none' },
                            '& .MuiSvgIcon-root': { color: logoColors.darkBlue },
                        }}
                    >
                        <MenuItem value="PENDING">PENDING</MenuItem>
                        <MenuItem value="CONFIRMED">CONFIRMED</MenuItem>
                        <MenuItem value="CANCELLED">CANCELLED</MenuItem>
                        <MenuItem value="CONFLICT">CONFLICT</MenuItem>
                        <MenuItem value="COMPLETED">COMPLETED</MenuItem>
                    </Select>
                </FormControl>
            </Box>

            <TableContainer sx={{
                backgroundColor: logoColors.primaryBlue,
                borderRadius: 1,
                overflowX: 'auto',
                overflowY: 'hidden'
            }}>
                <Table size="small" sx={{
                    width: '100%',
                    tableLayout: 'auto',
                    // Phone layout: each booking row becomes a stacked card (desktop table is untouched)
                    '@media (max-width:599.95px)': {
                        '&, & tbody': { display: 'block' },
                        '& thead': { display: 'none' },
                        '& tbody tr': {
                            display: 'block',
                            borderBottom: `3px solid ${logoColors.brightYellow}40`,
                            pb: 0.5,
                        },
                        '& tbody td': {
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: 2,
                            textAlign: 'right',
                            width: 'auto !important',
                            py: 0.75,
                            overflowWrap: 'anywhere',
                        },
                        '& tbody td[data-label]::before': {
                            content: 'attr(data-label)',
                            fontWeight: 700,
                            fontSize: '0.75rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.04em',
                            color: logoColors.lightYellow,
                            textAlign: 'left',
                            flexShrink: 0,
                        },
                        '& tbody td[colspan]': { display: 'block', textAlign: 'center' },
                    },
                }}>
                            <TableHead>
                                <TableRow sx={{ 
                                    backgroundColor: logoColors.brightYellow 
                                }}>
                                    <TableCell sx={{ 
                                        color: logoColors.darkBlue, 
                                        fontWeight: 'bold',
                                        borderBottom: `2px solid ${logoColors.darkBlue}`,
                                        width: '50px',
                                        padding: '8px'
                                    }}>
                                        <Checkbox
                                            checked={selectedBookings.length > 0 && selectedBookings.length === paginatedFilteredBookings.length}
                                            indeterminate={selectedBookings.length > 0 && selectedBookings.length < paginatedFilteredBookings.length}
                                            onChange={handleSelectAll}
                                            sx={{
                                                color: logoColors.darkBlue,
                                                '&.Mui-checked': {
                                                    color: logoColors.darkBlue,
                                                },
                                                '&.MuiCheckbox-indeterminate': {
                                                    color: logoColors.darkBlue,
                                                }
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell sx={{ 
                                        color: logoColors.darkBlue, 
                                        fontWeight: 'bold',
                                        borderBottom: `2px solid ${logoColors.darkBlue}`
                                    }}>
                                        <strong>ID</strong>
                                    </TableCell>
                                    <TableCell sx={{ 
                                        color: logoColors.darkBlue, 
                                        fontWeight: 'bold',
                                        borderBottom: `2px solid ${logoColors.darkBlue}`
                                    }}>
                                        <strong>Customer</strong>
                                    </TableCell>
                                    <TableCell sx={{ 
                                        color: logoColors.darkBlue, 
                                        fontWeight: 'bold',
                                        borderBottom: `2px solid ${logoColors.darkBlue}`
                                    }}>
                                        <strong>Contact</strong>
                                    </TableCell>
                                    <TableCell sx={{
                                        color: logoColors.darkBlue,
                                        fontWeight: 'bold',
                                        borderBottom: `2px solid ${logoColors.darkBlue}`
                                    }}>
                                        <strong>Opponent</strong>
                                    </TableCell>
                                    <TableCell sx={{
                                        color: logoColors.darkBlue,
                                        fontWeight: 'bold',
                                        borderBottom: `2px solid ${logoColors.darkBlue}`
                                    }}>
                                        <TableSortLabel
                                            active={sortConfig.field === 'date'}
                                            direction={sortConfig.field === 'date' ? sortConfig.direction : 'asc'}
                                            onClick={() => handleSort('date')}
                                            sx={{
                                                color: `${logoColors.darkBlue} !important`,
                                                fontWeight: 'bold',
                                                '& .MuiTableSortLabel-icon': {
                                                    color: `${logoColors.darkBlue} !important`,
                                                }
                                            }}
                                        >
                                            <strong>Date</strong>
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell sx={{
                                        color: logoColors.darkBlue,
                                        fontWeight: 'bold',
                                        borderBottom: `2px solid ${logoColors.darkBlue}`
                                    }}>
                                        <TableSortLabel
                                            active={sortConfig.field === 'time'}
                                            direction={sortConfig.field === 'time' ? sortConfig.direction : 'asc'}
                                            onClick={() => handleSort('time')}
                                            sx={{
                                                color: `${logoColors.darkBlue} !important`,
                                                fontWeight: 'bold',
                                                '& .MuiTableSortLabel-icon': {
                                                    color: `${logoColors.darkBlue} !important`,
                                                }
                                            }}
                                        >
                                            <strong>Time</strong>
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell sx={{ 
                                        color: logoColors.darkBlue, 
                                        fontWeight: 'bold',
                                        borderBottom: `2px solid ${logoColors.darkBlue}`
                                    }}>
                                        <strong>Court</strong>
                                    </TableCell>
                                    <TableCell sx={{ 
                                        color: logoColors.darkBlue, 
                                        fontWeight: 'bold',
                                        borderBottom: `2px solid ${logoColors.darkBlue}`
                                    }}>
                                        <strong>Amount (PKR)</strong>
                                    </TableCell>
                                    <TableCell sx={{
                                        color: logoColors.darkBlue,
                                        fontWeight: 'bold',
                                        borderBottom: `2px solid ${logoColors.darkBlue}`
                                    }}>
                                        <strong>Cash (PKR)</strong>
                                    </TableCell>
                                    <TableCell sx={{
                                        color: logoColors.darkBlue,
                                        fontWeight: 'bold',
                                        borderBottom: `2px solid ${logoColors.darkBlue}`
                                    }}>
                                        <strong>Online (PKR)</strong>
                                    </TableCell>
                                    <TableCell sx={{
                                        color: logoColors.darkBlue,
                                        fontWeight: 'bold',
                                        borderBottom: `2px solid ${logoColors.darkBlue}`
                                    }}>
                                        <strong>Remarks</strong>
                                    </TableCell>
                                    <TableCell sx={{
                                        color: logoColors.darkBlue,
                                        fontWeight: 'bold',
                                        borderBottom: `2px solid ${logoColors.darkBlue}`
                                    }}>
                                        <strong>Due</strong>
                                    </TableCell>
                                    <TableCell sx={{
                                        color: logoColors.darkBlue,
                                        fontWeight: 'bold',
                                        borderBottom: `2px solid ${logoColors.darkBlue}`
                                    }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <Typography sx={{ fontWeight: 'bold', color: logoColors.darkBlue }}>
                                                Status
                                            </Typography>
                                            <FormControl variant="standard" size="small" sx={{ minWidth: 40 }}>
                                                <Select
                                                    multiple
                                                    displayEmpty
                                                    value={statusFilter}
                                                    onChange={handleStatusFilterChange}
                                                    renderValue={(selected) => selected.length > 0 ? `(${selected.length})` : ''}
                                                    MenuProps={{
                                                        autoFocus: false,
                                                        PaperProps: {
                                                            sx: {
                                                                backgroundColor: logoColors.darkBlue,
                                                                '& .MuiMenuItem-root': {
                                                                    color: logoColors.brightYellow,
                                                                    '&:hover': {
                                                                        backgroundColor: logoColors.primaryBlue,
                                                                    },
                                                                    '&.Mui-selected': {
                                                                        backgroundColor: logoColors.primaryBlue,
                                                                        '&:hover': {
                                                                            backgroundColor: logoColors.primaryBlue,
                                                                        }
                                                                    }
                                                                }
                                                            }
                                                        }
                                                    }}
                                                    sx={{
                                                        color: logoColors.darkBlue,
                                                        fontWeight: 'bold',
                                                        '&:before': { borderBottom: 'none' },
                                                        '&:after': { borderBottom: 'none' },
                                                        '&:hover:not(.Mui-disabled):before': { borderBottom: 'none' },
                                                        '& .MuiSelect-select': {
                                                            paddingBottom: 0,
                                                            minHeight: 'auto',
                                                        },
                                                        '& .MuiSvgIcon-root': {
                                                            color: logoColors.darkBlue,
                                                        }
                                                    }}
                                                >
                                                    <MenuItem value="PENDING">PENDING</MenuItem>
                                                    <MenuItem value="CONFIRMED">CONFIRMED</MenuItem>
                                                    <MenuItem value="CANCELLED">CANCELLED</MenuItem>
                                                    <MenuItem value="CONFLICT">CONFLICT</MenuItem>
                                                    <MenuItem value="COMPLETED">COMPLETED</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={{ 
                                        color: logoColors.darkBlue, 
                                        fontWeight: 'bold',
                                        borderBottom: `2px solid ${logoColors.darkBlue}`
                                    }}>
                                        <strong>Actions</strong>
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {paginatedFilteredBookings.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={15} sx={{
                                            color: logoColors.brightYellow,
                                            textAlign: 'center',
                                            py: 4
                                        }}>
                                            No bookings found.
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    paginatedFilteredBookings.map((booking, index) => (
                                    <React.Fragment key={booking.id}>
                                    <TableRow
                                        hover
                                        sx={{
                                            backgroundColor: index % 2 === 0 ? logoColors.primaryBlue : logoColors.darkBlue,
                                            '&:hover': {
                                                backgroundColor: logoColors.lightYellow + '20' // 20% opacity
                                            }
                                        }}
                                    >
                                        <TableCell data-label="Select" sx={{ 
                                            borderBottom: `1px solid ${logoColors.brightYellow}30`,
                                            width: '50px',
                                            padding: '8px'
                                        }}>
                                            <Checkbox
                                                checked={selectedBookings.includes(booking.id)}
                                                onChange={() => handleSelectBooking(booking.id)}
                                                sx={{
                                                    color: logoColors.brightYellow,
                                                    '&.Mui-checked': {
                                                        color: logoColors.brightYellow,
                                                    }
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell data-label="ID" sx={{ 
                                            color: logoColors.brightYellow,
                                            borderBottom: `1px solid ${logoColors.brightYellow}30`
                                        }}>
                                            {booking.id}
                                        </TableCell>
                                        <TableCell data-label="Customer" sx={{ 
                                            color: logoColors.brightYellow,
                                            borderBottom: `1px solid ${logoColors.brightYellow}30`
                                        }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                                                {booking.customerName}
                                                {booking.isShared ? (
                                                    <Chip
                                                        size="small"
                                                        icon={expandedLedgerId === booking.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                                        label="Split"
                                                        onClick={() => toggleLedgerPanel(booking)}
                                                        sx={{
                                                            height: 22, fontSize: '0.7rem', fontWeight: 700, cursor: 'pointer',
                                                            bgcolor: `${logoColors.lightYellow}25`, color: logoColors.lightYellow,
                                                            '& .MuiChip-icon': { color: logoColors.lightYellow, fontSize: '0.9rem' },
                                                        }}
                                                    />
                                                ) : (
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => makeShared(booking)}
                                                        disabled={savingLedger}
                                                        title="Split this booking between people"
                                                        sx={{ color: `${logoColors.brightYellow}70`, p: 0.25 }}
                                                    >
                                                        <GroupsIcon sx={{ fontSize: '1rem' }} />
                                                    </IconButton>
                                                )}
                                            </Box>
                                        </TableCell>
                                        <TableCell data-label="Contact" sx={{ 
                                            color: logoColors.brightYellow,
                                            borderBottom: `1px solid ${logoColors.brightYellow}30`
                                        }}>
                                            <span>
                                                {booking.customerEmail}<br />
                                                <small style={{ opacity: 0.8 }}>{booking.customerPhone}</small>
                                            </span>
                                        </TableCell>
                                        <TableCell data-label="Opponent" sx={{
                                            color: logoColors.brightYellow,
                                            borderBottom: `1px solid ${logoColors.brightYellow}30`
                                        }}>
                                            {editingBooking === booking.id ? (
                                                <Input
                                                    placeholder="Opponent name"
                                                    value={editForm.opponentName ?? ''}
                                                    onChange={(e) => handleEditFormChange('opponentName', e.target.value)}
                                                    sx={{ color: logoColors.brightYellow, fontSize: '0.85rem', width: '110px' }}
                                                />
                                            ) : (
                                                booking.opponentName || '-'
                                            )}
                                        </TableCell>
                                        <TableCell data-label="Date" sx={{
                                            color: logoColors.brightYellow,
                                            borderBottom: `1px solid ${logoColors.brightYellow}30`
                                        }}>
                                            {editingBooking === booking.id ? (
                                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                    <DatePicker
                                                        value={dayjs(editForm.bookingDate || booking.bookingDate)}
                                                        onChange={(newValue) => handleEditFormChange('bookingDate', newValue ? newValue.format('YYYY-MM-DD') : '')}
                                                        format="DD/MM/YYYY"
                                                        slotProps={{
                                                            ...datePickerPopperStyles,
                                                            textField: {
                                                                size: "small",
                                                                InputProps: {
                                                                    sx: {
                                                                        color: logoColors.brightYellow,
                                                                        fontSize: '0.85rem',
                                                                        '& input': {
                                                                            color: `${logoColors.brightYellow} !important`,
                                                                            WebkitTextFillColor: `${logoColors.brightYellow} !important`,
                                                                            fontWeight: '700 !important',
                                                                            padding: '6px 8px'
                                                                        }
                                                                    }
                                                                },
                                                                sx: {
                                                                    width: '160px',
                                                                    '& .MuiOutlinedInput-root': {
                                                                        '& fieldset': {
                                                                            borderColor: logoColors.brightYellow,
                                                                        },
                                                                        '&:hover fieldset': {
                                                                            borderColor: logoColors.lightYellow,
                                                                        },
                                                                        '&.Mui-focused fieldset': {
                                                                            borderColor: logoColors.lightYellow,
                                                                        }
                                                                    },
                                                                    '& .MuiSvgIcon-root': {
                                                                        color: `${logoColors.brightYellow} !important`
                                                                    }
                                                                }
                                                            }
                                                        }}
                                                    />
                                                </LocalizationProvider>
                                            ) : (
                                                dayjs(booking.bookingDate).format('MMM DD, YYYY')
                                            )}
                                        </TableCell>
                                        <TableCell data-label="Time" sx={{ 
                                            color: logoColors.brightYellow,
                                            borderBottom: `1px solid ${logoColors.brightYellow}30`
                                        }}>
                                            {editingBooking === booking.id ? (
                                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                                    <Input
                                                        type="time"
                                                        value={editForm.startTime || booking.startTime}
                                                        onChange={(e) => handleEditFormChange('startTime', e.target.value)}
                                                        sx={{ 
                                                            color: logoColors.brightYellow,
                                                            fontSize: '0.8rem',
                                                            width: '80px'
                                                        }}
                                                    />
                                                    <span>-</span>
                                                    <Input
                                                        type="time"
                                                        value={editForm.endTime || booking.endTime}
                                                        onChange={(e) => handleEditFormChange('endTime', e.target.value)}
                                                        sx={{ 
                                                            color: logoColors.brightYellow,
                                                            fontSize: '0.8rem',
                                                            width: '80px'
                                                        }}
                                                    />
                                                </Box>
                                            ) : (
                                                formatTimeRange(booking.startTime, booking.endTime)
                                            )}
                                        </TableCell>
                                        <TableCell data-label="Court" sx={{ 
                                            color: logoColors.brightYellow,
                                            borderBottom: `1px solid ${logoColors.brightYellow}30`
                                        }}>
                                            {editingBooking === booking.id ? (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <span>Court</span>
                                                    <Input
                                                        type="number"
                                                        value={editForm.courtNumber || booking.courtNumber}
                                                        onChange={(e) => handleEditFormChange('courtNumber', parseInt(e.target.value))}
                                                        inputProps={{ min: 1, max: 10 }}
                                                        sx={{ 
                                                            color: logoColors.brightYellow,
                                                            fontSize: '0.9rem',
                                                            width: '50px',
                                                            '& input': {
                                                                color: logoColors.brightYellow,
                                                                textAlign: 'center'
                                                            }
                                                        }}
                                                    />
                                                </Box>
                                            ) : (
                                                `Court ${booking.courtNumber}`
                                            )}
                                        </TableCell>
                                        <TableCell data-label="Amount (PKR)" sx={{ 
                                            color: logoColors.brightYellow,
                                            borderBottom: `1px solid ${logoColors.brightYellow}30`
                                        }}>
                                            {editingBooking === booking.id ? (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <span>PKR</span>
                                                    <Input
                                                        type="number"
                                                        value={editForm.totalAmount || booking.totalAmount}
                                                        onChange={(e) => handleEditFormChange('totalAmount', parseFloat(e.target.value))}
                                                        sx={{ 
                                                            color: logoColors.brightYellow,
                                                            fontSize: '0.9rem',
                                                            width: '80px'
                                                        }}
                                                    />
                                                </Box>
                                            ) : (
                                                booking.totalAmount
                                            )}
                                        </TableCell>
                                        <TableCell data-label="Cash (PKR)" sx={{
                                            color: logoColors.brightYellow,
                                            borderBottom: `1px solid ${logoColors.brightYellow}30`
                                        }}>
                                            {editingBooking === booking.id ? (
                                                <Input
                                                    type="number"
                                                    value={editForm.paidCashAmount ?? (booking.paidCashAmount || 0)}
                                                    onChange={(e) => handleEditFormChange('paidCashAmount', parseFloat(e.target.value) || 0)}
                                                    sx={{ color: logoColors.brightYellow, fontSize: '0.9rem', width: '80px' }}
                                                />
                                            ) : (
                                                booking.paidCashAmount || 0
                                            )}
                                        </TableCell>
                                        <TableCell data-label="Online (PKR)" sx={{
                                            color: logoColors.brightYellow,
                                            borderBottom: `1px solid ${logoColors.brightYellow}30`
                                        }}>
                                            {editingBooking === booking.id ? (
                                                <Input
                                                    type="number"
                                                    value={editForm.paidOnlineAmount ?? (booking.paidOnlineAmount || 0)}
                                                    onChange={(e) => handleEditFormChange('paidOnlineAmount', parseFloat(e.target.value) || 0)}
                                                    sx={{ color: logoColors.brightYellow, fontSize: '0.9rem', width: '80px' }}
                                                />
                                            ) : (
                                                booking.paidOnlineAmount || 0
                                            )}
                                        </TableCell>
                                        <TableCell data-label="Remarks" sx={{
                                            color: logoColors.brightYellow,
                                            borderBottom: `1px solid ${logoColors.brightYellow}30`
                                        }}>
                                            {editingBooking === booking.id ? (
                                                <Input
                                                    placeholder="Remarks"
                                                    value={editForm.remarks ?? ''}
                                                    onChange={(e) => handleEditFormChange('remarks', e.target.value)}
                                                    sx={{ color: logoColors.brightYellow, fontSize: '0.85rem', width: '140px' }}
                                                />
                                            ) : (
                                                booking.remarks || '-'
                                            )}
                                        </TableCell>
                                        <TableCell data-label="Due" sx={{
                                            borderBottom: `1px solid ${logoColors.brightYellow}30`
                                        }}>
                                            {(() => {
                                                const dueAmount = editingBooking === booking.id
                                                    ? Math.max((editForm.totalAmount ?? booking.totalAmount) - ((editForm.paidCashAmount ?? booking.paidCashAmount ?? 0) + (editForm.paidOnlineAmount ?? booking.paidOnlineAmount ?? 0)), 0)
                                                    : (booking.amountDue ?? Math.max(booking.totalAmount - ((booking.paidCashAmount || 0) + (booking.paidOnlineAmount || 0)), 0));
                                                return (
                                                    <Chip
                                                        label={`PKR ${dueAmount}`}
                                                        color={dueAmount > 0 ? 'warning' : 'success'}
                                                        size="small"
                                                    />
                                                );
                                            })()}
                                        </TableCell>
                                        <TableCell data-label="Status" sx={{
                                            borderBottom: `1px solid ${logoColors.brightYellow}30`
                                        }}>
                                            {editingBooking === booking.id ? (
                                                <FormControl size="small" sx={{ minWidth: 130 }}>
                                                    <Select
                                                        value={editForm.status || booking.status}
                                                        onChange={(e) => handleEditFormChange('status', e.target.value)}
                                                        sx={{
                                                            color: logoColors.brightYellow,
                                                            fontSize: '0.8rem',
                                                            '& .MuiOutlinedInput-notchedOutline': {
                                                                borderColor: logoColors.brightYellow,
                                                            },
                                                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                                                borderColor: logoColors.lightYellow,
                                                            },
                                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                                borderColor: logoColors.lightYellow,
                                                            },
                                                            '& .MuiSvgIcon-root': {
                                                                color: logoColors.brightYellow,
                                                            }
                                                        }}
                                                        MenuProps={{
                                                            PaperProps: {
                                                                sx: {
                                                                    backgroundColor: logoColors.darkBlue,
                                                                    '& .MuiMenuItem-root': {
                                                                        color: logoColors.brightYellow,
                                                                        '&:hover': {
                                                                            backgroundColor: logoColors.primaryBlue,
                                                                        },
                                                                        '&.Mui-selected': {
                                                                            backgroundColor: logoColors.primaryBlue,
                                                                        }
                                                                    }
                                                                }
                                                            }
                                                        }}
                                                    >
                                                        <MenuItem value="PENDING">PENDING</MenuItem>
                                                        <MenuItem value="CONFIRMED">CONFIRMED</MenuItem>
                                                        <MenuItem value="CANCELLED">CANCELLED</MenuItem>
                                                        <MenuItem value="CONFLICT">CONFLICT</MenuItem>
                                                        <MenuItem value="COMPLETED">COMPLETED</MenuItem>
                                                        <MenuItem value="NO_SHOW">NO_SHOW</MenuItem>
                                                    </Select>
                                                </FormControl>
                                            ) : (
                                                <Chip 
                                                    label={booking.status || 'CONFIRMED'} 
                                                    color={getStatusColor(booking.status)}
                                                    size="small"
                                                />
                                            )}
                                        </TableCell>
                                        <TableCell data-label="Actions" sx={{ 
                                            borderBottom: `1px solid ${logoColors.brightYellow}30`
                                        }}>
                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                                {booking.status === 'PENDING' && (
                                                    <IconButton
                                                        onClick={() => setConfirmDialog({ open: true, booking })}
                                                        size="small"
                                                        disabled={updatingStatus === booking.id}
                                                        sx={{
                                                            color: logoColors.brightYellow,
                                                            backgroundColor: logoColors.darkBlue + '20',
                                                            boxShadow: 'none',
                                                            '&:active': {
                                                                backgroundColor: logoColors.darkBlue + '30',
                                                            },
                                                            '&:hover': {
                                                                backgroundColor: logoColors.brightYellow + '20',
                                                                color: logoColors.brightYellow
                                                            }
                                                        }}
                                                        title="Confirm Payment"
                                                    >
                                                        <ConfirmIcon />
                                                    </IconButton>
                                                )}
                                                {editingBooking === booking.id ? (
                                                    <>
                                                        <IconButton
                                                            onClick={() => handleConfirmEdit(booking.id)}
                                                            size="small"
                                                            disabled={updatingStatus === booking.id}
                                                            sx={{
                                                                color: '#4caf50',
                                                                backgroundColor: '#4caf5020',
                                                                boxShadow: 'none',
                                                                '&:active': {
                                                                    backgroundColor: '#4caf5030',
                                                                },
                                                                '&:hover': {
                                                                    backgroundColor: '#4caf5020',
                                                                    color: '#4caf50'
                                                                }
                                                            }}
                                                            title="Confirm Changes"
                                                        >
                                                            <SaveIcon />
                                                        </IconButton>
                                                        <IconButton
                                                            onClick={handleCancelEdit}
                                                            size="small"
                                                            sx={{
                                                                color: '#f44336',
                                                                backgroundColor: '#f4433620',
                                                                boxShadow: 'none',
                                                                '&:active': {
                                                                    backgroundColor: '#f4433630',
                                                                },
                                                                '&:hover': {
                                                                    backgroundColor: '#f4433620',
                                                                    color: '#f44336'
                                                                }
                                                            }}
                                                            title="Cancel Edit"
                                                        >
                                                            <CloseIcon />
                                                        </IconButton>
                                                    </>
                                                ) : (
                                                    <>
                                                        {/* Quick Reactivate button for CANCELLED bookings */}
                                                        {booking.status === 'CANCELLED' && (
                                                            <IconButton
                                                                onClick={async () => {
                                                                    try {
                                                                        setUpdatingStatus(booking.id);
                                                                        await bookingAPI.updateBookingStatus(booking.id, 'PENDING');
                                                                        toast.success('Booking reactivated to PENDING');
                                                                        fetchBookings();
                                                                    } catch (error) {
                                                                        toast.error('Failed to reactivate booking');
                                                                    } finally {
                                                                        setUpdatingStatus(null);
                                                                    }
                                                                }}
                                                                size="small"
                                                                disabled={updatingStatus === booking.id}
                                                                sx={{
                                                                    color: '#4caf50',
                                                                    backgroundColor: '#4caf5020',
                                                                    boxShadow: 'none',
                                                                    '&:active': {
                                                                        backgroundColor: '#4caf5030',
                                                                    },
                                                                    '&:hover': {
                                                                        backgroundColor: '#4caf5020',
                                                                        color: '#4caf50'
                                                                    }
                                                                }}
                                                                title="Quick Reactivate (Set to PENDING)"
                                                            >
                                                                <ReplayIcon />
                                                            </IconButton>
                                                        )}
                                                        
                                                        {/* Edit button - Available for all statuses */}
                                                        <IconButton
                                                            onClick={() => handleEditBooking(booking)}
                                                            size="small"
                                                            disabled={updatingStatus === booking.id}
                                                            sx={{
                                                                color: '#2196f3',
                                                                backgroundColor: '#2196f320',
                                                                boxShadow: 'none',
                                                                '&:active': {
                                                                    backgroundColor: '#2196f330',
                                                                },
                                                                '&:hover': {
                                                                    backgroundColor: '#2196f320',
                                                                    color: '#2196f3'
                                                                }
                                                            }}
                                                            title={`Edit Details & Status${booking.status === 'COMPLETED' || booking.status === 'CANCELLED' ? ' (Sensitive)' : ''}`}
                                                        >
                                                            <EditIcon />
                                                        </IconButton>
                                                        
                                                        {/* Cancel button - Available for all except CANCELLED */}
                                                        {booking.status !== 'CANCELLED' && (
                                                            <IconButton
                                                                onClick={() => setCancelDialog({ open: true, booking })}
                                                                size="small"
                                                                disabled={updatingStatus === booking.id}
                                                                sx={{
                                                                    color: '#ff9800',
                                                                    backgroundColor: '#ff980020',
                                                                    boxShadow: 'none',
                                                                    '&:active': {
                                                                        backgroundColor: '#ff980030',
                                                                    },
                                                                    '&:hover': {
                                                                        backgroundColor: '#ff980020',
                                                                        color: '#ff9800'
                                                                    }
                                                                }}
                                                                title={`Cancel Booking${booking.status === 'COMPLETED' ? ' (Sensitive Operation)' : ''}`}
                                                            >
                                                                <CancelIcon />
                                                            </IconButton>
                                                        )}
                                                    </>
                                                )}
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                    {booking.isShared && (
                                        <TableRow sx={{ backgroundColor: `${logoColors.darkBlue}` }}>
                                            <TableCell colSpan={15} sx={{ p: 0, borderBottom: `1px solid ${logoColors.brightYellow}30` }}>
                                                <Collapse in={expandedLedgerId === booking.id} timeout="auto" unmountOnExit>
                                                    <Box sx={{ p: { xs: 2, sm: 3 }, background: 'rgba(255,255,255,0.03)' }}>
                                                        {ledgerDraft && expandedLedgerId === booking.id && (
                                                            <>
                                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 1 }}>
                                                                    <Typography sx={{ color: logoColors.lightYellow, fontWeight: 700 }}>
                                                                        Split between {ledgerSplitCount(ledgerDraft)} people &mdash; Rs {ledgerShare(booking, ledgerDraft).toLocaleString()} each
                                                                    </Typography>
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                        <Typography sx={{ color: `${logoColors.brightYellow}90`, fontSize: '0.85rem' }}>
                                                                            + guests
                                                                        </Typography>
                                                                        <IconButton size="small" disabled={!ledgerDraft.additionalGuestsCount}
                                                                            onClick={() => setLedgerDraft(prev => ({ ...prev, additionalGuestsCount: Math.max((prev.additionalGuestsCount || 0) - 1, 0) }))}
                                                                            sx={{ color: logoColors.brightYellow, border: `1px solid ${logoColors.lightYellow}40` }}>
                                                                            <RemoveIcon fontSize="small" />
                                                                        </IconButton>
                                                                        <Typography sx={{ color: logoColors.lightYellow, fontWeight: 700, minWidth: 18, textAlign: 'center' }}>
                                                                            {ledgerDraft.additionalGuestsCount || 0}
                                                                        </Typography>
                                                                        <IconButton size="small"
                                                                            onClick={() => setLedgerDraft(prev => ({ ...prev, additionalGuestsCount: (prev.additionalGuestsCount || 0) + 1 }))}
                                                                            sx={{ color: logoColors.brightYellow, border: `1px solid ${logoColors.lightYellow}40` }}>
                                                                            <AddIcon fontSize="small" />
                                                                        </IconButton>
                                                                    </Box>
                                                                </Box>

                                                                <Stack spacing={1.25}>
                                                                    {ledgerDraft.participants.map((p, i) => {
                                                                        const share = ledgerShare(booking, ledgerDraft);
                                                                        const remaining = Math.max(Math.round((share - (p.amountPaid || 0)) * 100) / 100, 0);
                                                                        return (
                                                                            <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                                                                                <Avatar sx={{ width: 28, height: 28, fontSize: '0.75rem', fontWeight: 700, bgcolor: `${logoColors.brightYellow}25`, color: logoColors.brightYellow }}>
                                                                                    {i + 1}
                                                                                </Avatar>
                                                                                <TextField
                                                                                    size="small"
                                                                                    placeholder="Name"
                                                                                    value={p.name}
                                                                                    onChange={(e) => updateLedgerParticipant(i, 'name', e.target.value)}
                                                                                    sx={{ flex: '1 1 130px', minWidth: 110, input: { color: logoColors.brightYellow } }}
                                                                                />
                                                                                <TextField
                                                                                    size="small"
                                                                                    placeholder={i === 0 ? 'Phone (required)' : 'Phone (optional)'}
                                                                                    value={p.phone}
                                                                                    onChange={(e) => updateLedgerParticipant(i, 'phone', e.target.value)}
                                                                                    sx={{ flex: '1 1 150px', minWidth: 140, input: { color: logoColors.brightYellow } }}
                                                                                />
                                                                                <TextField
                                                                                    size="small"
                                                                                    type="number"
                                                                                    label="Paid"
                                                                                    value={p.amountPaid}
                                                                                    onChange={(e) => updateLedgerParticipant(i, 'amountPaid', parseFloat(e.target.value) || 0)}
                                                                                    sx={{ width: 100, input: { color: logoColors.brightYellow } }}
                                                                                />
                                                                                <Chip
                                                                                    size="small"
                                                                                    label={remaining > 0 ? `Owes Rs ${remaining.toLocaleString()}` : 'Paid up'}
                                                                                    sx={{
                                                                                        fontWeight: 600,
                                                                                        bgcolor: remaining > 0 ? '#ff980025' : '#4caf5025',
                                                                                        color: remaining > 0 ? '#ff9800' : '#4caf50',
                                                                                    }}
                                                                                />
                                                                                <IconButton
                                                                                    size="small"
                                                                                    disabled={!p.phone || !p.phone.trim()}
                                                                                    component="a"
                                                                                    href={p.phone ? whatsAppReminderLink(booking, p, remaining) : undefined}
                                                                                    target="_blank"
                                                                                    rel="noopener noreferrer"
                                                                                    title="Send WhatsApp reminder"
                                                                                    sx={{ color: '#25D366', '&.Mui-disabled': { color: `${logoColors.brightYellow}30` } }}
                                                                                >
                                                                                    <WhatsAppIcon fontSize="small" />
                                                                                </IconButton>
                                                                            </Box>
                                                                        );
                                                                    })}
                                                                </Stack>

                                                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2.5 }}>
                                                                    <Button size="small" onClick={() => toggleLedgerPanel(booking)} sx={{ color: logoColors.brightYellow }}>
                                                                        Close
                                                                    </Button>
                                                                    <Button
                                                                        size="small"
                                                                        variant="contained"
                                                                        disabled={savingLedger}
                                                                        onClick={() => saveLedger(booking)}
                                                                        sx={{
                                                                            background: `linear-gradient(45deg, ${logoColors.brightYellow} 0%, ${logoColors.lightYellow} 100%)`,
                                                                            color: logoColors.darkBlue, fontWeight: 700,
                                                                        }}
                                                                    >
                                                                        {savingLedger ? 'Saving...' : 'Save split & ledger'}
                                                                    </Button>
                                                                </Box>
                                                            </>
                                                        )}
                                                    </Box>
                                                </Collapse>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                    </React.Fragment>
                                ))
                                )}
                            </TableBody>
                        </Table>
            </TableContainer>
            
            {/* Pagination */}
            <TablePagination
                component="div"
                count={filteredBookings.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25, 50]}
                sx={{
                    color: logoColors.brightYellow,
                    // Phones: let the pagination row wrap instead of overflowing the screen
                    '@media (max-width:599.95px)': {
                        '& .MuiTablePagination-toolbar': { flexWrap: 'wrap', justifyContent: 'center', px: 0 },
                        '& .MuiTablePagination-spacer': { display: 'none' },
                        '& .MuiTablePagination-actions': { ml: 0 },
                    },
                    '& .MuiTablePagination-selectLabel': {
                        color: logoColors.brightYellow,
                    },
                    '& .MuiTablePagination-displayedRows': {
                        color: logoColors.brightYellow,
                    },
                    '& .MuiTablePagination-select': {
                        color: logoColors.brightYellow,
                        '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: logoColors.brightYellow,
                        },
                    },
                    '& .MuiIconButton-root': {
                        color: logoColors.brightYellow,
                        '&:hover': {
                            backgroundColor: `${logoColors.brightYellow}20`,
                        },
                        '&.Mui-disabled': {
                            color: `${logoColors.brightYellow}40`,
                        },
                    },
                }}
            />

            <Dialog
                open={confirmDialog.open}
                onClose={() => setConfirmDialog({ open: false, booking: null })}
                PaperProps={{
                    sx: {
                        backgroundColor: logoColors.darkBlue,
                        color: logoColors.brightYellow
                    }
                }}
            >
                <DialogTitle sx={{ color: logoColors.brightYellow,  fontWeight: 'bold', fontSize: '1.2rem' , textAlign: 'center',   mb: 2 }}>
                    Confirm Payment
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ color: logoColors.brightYellow }}>
                        Mark the booking for {confirmDialog.booking?.customerName} as CONFIRMED?
                        This will indicate that advance payment has been received.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button 
                        onClick={() => setConfirmDialog({ open: false, booking: null })}
                        sx={{ color: logoColors.brightYellow }}
                    >
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleConfirmPayment}
                        disabled={updatingStatus === confirmDialog.booking?.id}
                        sx={{ 
                            color: logoColors.brightYellow,
                            '&:hover': {
                                backgroundColor: logoColors.brightYellow + '20'
                            }
                        }}
                    >
                        {updatingStatus === confirmDialog.booking?.id ? 'Updating...' : 'Confirm Payment'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={cancelDialog.open}
                onClose={() => setCancelDialog({ open: false, booking: null })}
                PaperProps={{
                    sx: {
                        backgroundColor: logoColors.darkBlue,
                        color: logoColors.brightYellow
                    }
                }}
            >
                <DialogTitle sx={{ color: '#ff9800', fontWeight: 'bold', fontSize: '1.2rem', textAlign: 'center', mb: 2 }}>
                    Cancel Booking
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ color: logoColors.brightYellow }}>
                        Are you sure you want to cancel the booking for {cancelDialog.booking?.customerName}?
                        A cancellation email will be sent to the customer.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button 
                        onClick={() => setCancelDialog({ open: false, booking: null })}
                        sx={{ color: logoColors.brightYellow }}
                    >
                        No, Keep Booking
                    </Button>
                    <Button 
                        onClick={handleCancelBooking}
                        disabled={updatingStatus === cancelDialog.booking?.id}
                        sx={{ 
                            color: '#ff9800',
                            '&:hover': {
                                backgroundColor: '#ff980020'
                            }
                        }}
                    >
                        {updatingStatus === cancelDialog.booking?.id ? 'Cancelling...' : 'Yes, Cancel Booking'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Bulk Confirmation Dialog */}
            <Dialog
                open={bulkConfirmDialog.open}
                onClose={() => setBulkConfirmDialog({ open: false, bookings: [] })}
                PaperProps={{
                    sx: {
                        backgroundColor: logoColors.darkBlue,
                        color: logoColors.brightYellow
                    }
                }}
            >
                <DialogTitle sx={{ color: logoColors.brightYellow, fontWeight: 'bold', fontSize: '1.2rem', textAlign: 'center', mb: 2 }}>
                    Confirm Multiple Bookings
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ color: logoColors.brightYellow, mb: 2 }}>
                        Are you sure you want to confirm {bulkConfirmDialog.bookings.length} PENDING booking(s)?
                        This will mark them as CONFIRMED and send confirmation emails to customers.
                    </DialogContentText>
                    <Box sx={{ maxHeight: '200px', overflowY: 'auto' }}>
                        {bulkConfirmDialog.bookings.map(booking => (
                            <Box key={booking.id} sx={{ 
                                backgroundColor: logoColors.primaryBlue + '30',
                                p: 1,
                                mb: 1,
                                borderRadius: 1,
                                border: `1px solid ${logoColors.brightYellow}30`
                            }}>
                                <Typography variant="body2" sx={{ color: logoColors.brightYellow }}>
                                    <strong>ID {booking.id}:</strong> {booking.customerName} - {booking.bookingDate} {formatTimeRange(booking.startTime, booking.endTime)}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button 
                        onClick={() => setBulkConfirmDialog({ open: false, bookings: [] })}
                        sx={{ color: logoColors.brightYellow }}
                    >
                        Cancel
                    </Button>
                    <Button 
                        onClick={executeBulkConfirm}
                        disabled={bulkProcessing}
                        sx={{ 
                            color: logoColors.brightYellow,
                            '&:hover': {
                                backgroundColor: logoColors.brightYellow + '20'
                            }
                        }}
                    >
                        {bulkProcessing ? 'Confirming...' : `Confirm ${bulkConfirmDialog.bookings.length} Booking(s)`}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Bulk Cancellation Dialog */}
            <Dialog
                open={bulkCancelDialog.open}
                onClose={() => setBulkCancelDialog({ open: false, bookings: [] })}
                PaperProps={{
                    sx: {
                        backgroundColor: logoColors.darkBlue,
                        color: logoColors.brightYellow
                    }
                }}
            >
                <DialogTitle sx={{ color: '#ff9800', fontWeight: 'bold', fontSize: '1.2rem', textAlign: 'center', mb: 2 }}>
                    Cancel Multiple Bookings
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ color: logoColors.brightYellow, mb: 2 }}>
                        Are you sure you want to cancel {bulkCancelDialog.bookings.length} booking(s)?
                        Cancellation emails will be sent to customers and admin.
                    </DialogContentText>
                    <Box sx={{ maxHeight: '200px', overflowY: 'auto' }}>
                        {bulkCancelDialog.bookings.map(booking => (
                            <Box key={booking.id} sx={{ 
                                backgroundColor: logoColors.primaryBlue + '30',
                                p: 1,
                                mb: 1,
                                borderRadius: 1,
                                border: `1px solid ${logoColors.brightYellow}30`
                            }}>
                                <Typography variant="body2" sx={{ color: logoColors.brightYellow }}>
                                    <strong>ID {booking.id}:</strong> {booking.customerName} - {booking.bookingDate} {formatTimeRange(booking.startTime, booking.endTime)} ({booking.status})
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button 
                        onClick={() => setBulkCancelDialog({ open: false, bookings: [] })}
                        sx={{ color: logoColors.brightYellow }}
                    >
                        No, Keep Bookings
                    </Button>
                    <Button 
                        onClick={executeBulkCancel}
                        disabled={bulkProcessing}
                        sx={{ 
                            color: '#ff9800',
                            '&:hover': {
                                backgroundColor: '#ff980020'
                            }
                        }}
                    >
                        {bulkProcessing ? 'Cancelling...' : `Yes, Cancel ${bulkCancelDialog.bookings.length} Booking(s)`}
                    </Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
};

export default BookingList;